import React from 'react';
import { LocalHourInsight } from './LocalHourInsight';
import { summarizeLocalHours } from '../../utils/localHours';
import type { LocalHourPoint } from '../../types/traffic';
interface LocalHourChartProps {
  points: LocalHourPoint[];
}
function hourLabel(hour: number): string {
  if (hour === 0) return '12a';
  if (hour === 12) return '12p';
  return hour < 12 ? `${hour}a` : `${hour - 12}p`;
}

/**
 * Requests plotted against the visitor's OWN clock rather than UTC, so a spike at
 * "9am local" reads as the start of someone's working day regardless of region.
 * The lighter overlay is the tunnelled subset.
 */
export function LocalHourChart({
  points
}: LocalHourChartProps) {
  const total = points.reduce((sum, point) => sum + point.requests, 0);
  if (total === 0) {
    return <div className="py-10 text-center text-xs leading-relaxed text-muted-foreground" data-id="element-172" data-mp="2jqrbn2816rnq">
        <p className="font-medium text-foreground" data-id="element-173" data-mp="2ns2yizzisys8">No local-time data yet</p>
        <p className="mt-1.5" data-id="element-174" data-mp="2nq0a2ke0ndpm">
          Historical rows can be filled in — they already store the visitor&rsquo;s time zone, so
          running <span className="font-mono" data-id="element-175" data-mp="21iijofn0a4ha">supabase/backfill.sql</span> derives the local hour
          retroactively.
        </p>
      </div>;
  }
  const byHour = new Map(points.map(point => [point.hour, point]));
  const hours = Array.from({
    length: 24
  }, (_, hour) => byHour.get(hour) ?? null);
  const peak = Math.max(...points.map(point => point.requests), 1);
  const insight = summarizeLocalHours(points);
  return <div data-id="element-176" data-mp="2kaktp8556mik">
      {/* Fixed-height track: bars must stretch, so the row cannot use items-end. */}
      <div className="flex h-40 items-stretch gap-1 sm:h-48" role="img" aria-label="Requests by visitor local hour" data-id="element-177" data-mp="2fhrpqmhhzi7j">
        {hours.map((point, hour) => {
        const requests = point?.requests ?? 0;
        const tunnelled = point?.tunnelledRequests ?? 0;
        const height = requests / peak * 100;
        const tunnelledHeight = requests > 0 ? tunnelled / requests * 100 : 0;
        return <div key={hour} className="flex flex-1 flex-col justify-end" data-id="element-178" data-mp="11d7r4axvh3pi" data-mpi={hour}>
              <div className="relative w-full rounded-sm bg-primary" style={{
            height: `${requests > 0 ? Math.max(height, 2) : 0}%`
          }} title={`${hourLabel(hour)} local — ${requests.toLocaleString()} requests, ${tunnelled.toLocaleString()} tunnelled`} data-id="element-179" data-mp="169evhzame75c">
                {tunnelled > 0 && <div className="absolute inset-x-0 top-0 rounded-sm bg-muted-foreground/50" style={{
              height: `${tunnelledHeight}%`
            }} data-id="element-180" data-mp="mmgzb2wa64me" />}
              </div>
            </div>;
      })}
      </div>

      <div className="mt-2 flex gap-1" aria-hidden="true" data-id="element-181" data-mp="2307fv94l6lcx">
        {hours.map((_point, hour) => <span key={hour} className="flex-1 text-center font-mono text-[10px] text-muted-foreground" data-id="element-182" data-mp="27za8fu3dqz40" data-mpi={hour}>
            {hour % 3 === 0 ? hourLabel(hour) : ''}
          </span>)}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground" data-id="element-183" data-mp="3nj17zsxr7pgi">
        <span className="flex items-center gap-1.5" data-id="element-184" data-mp="6202xnqpkrik">
          <span className="h-2.5 w-2.5 rounded-sm bg-primary" aria-hidden="true" data-id="element-185" data-mp="63uql9td6xqc" />
          All requests
        </span>
        <span className="flex items-center gap-1.5" data-id="element-186" data-mp="f2g7z1n3o6pj">
          <span className="h-2.5 w-2.5 rounded-sm bg-muted-foreground/50" aria-hidden="true" data-id="element-187" data-mp="f0v8qypubzlc" />
          Tunnelled subset
        </span>
        <span className="font-mono" data-id="element-188" data-mp="p0m5l71viqol">peak {peak.toLocaleString()}/h</span>
      </div>

      {insight && <LocalHourInsight insight={insight} data-id="element-189" data-mp="oy9n8ss7eeb0" />}
    </div>;
}